import {
  deriveSupportTicketTitle,
  getSupportTicketFallbackTitle,
  PostHogSupportError,
  type SelectedSupportTicket,
} from './client';
import type { SupportIdentity, SupportMessage, SupportTicket } from './types';

export class SupportIdentityRequestError extends Error {
  constructor(readonly kind: 'signed-out' | 'unavailable') {
    super(kind);
    this.name = 'SupportIdentityRequestError';
  }
}

const INITIAL_SUPPORT_POLL_DELAY_MS = 5_000;
const MAX_SUPPORT_POLL_DELAY_MS = 60_000;

export function nextSupportPollDelay(
  currentDelayMs: number,
  succeeded: boolean
): number {
  return succeeded
    ? INITIAL_SUPPORT_POLL_DELAY_MS
    : Math.min(currentDelayMs * 2, MAX_SUPPORT_POLL_DELAY_MS);
}

export function startSupportPolling(poll: () => Promise<boolean>): () => void {
  let disposed = false;
  let delayMs = INITIAL_SUPPORT_POLL_DELAY_MS;
  let timeoutId: number;
  const schedule = () => {
    timeoutId = window.setTimeout(async () => {
      if (disposed) return;
      if (document.visibilityState === 'visible') {
        delayMs = nextSupportPollDelay(delayMs, await poll());
      }
      if (!disposed) schedule();
    }, delayMs);
  };
  schedule();
  return () => {
    disposed = true;
    window.clearTimeout(timeoutId);
  };
}

function isSupportIdentity(value: unknown): value is SupportIdentity {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.distinctId === 'string' &&
    typeof candidate.identityHash === 'string' &&
    /^[a-f0-9]{64}$/.test(candidate.identityHash)
  );
}

export async function fetchPostHogSupportIdentity(
  expectedUserId: string,
  signal: AbortSignal
): Promise<SupportIdentity> {
  const response = await fetch('/api/support/identity', {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (response.status === 401)
    throw new SupportIdentityRequestError('signed-out');
  if (!response.ok) throw new SupportIdentityRequestError('unavailable');

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new SupportIdentityRequestError('unavailable');
  }
  if (!isSupportIdentity(body) || body.distinctId !== expectedUserId) {
    throw new SupportIdentityRequestError('unavailable');
  }
  return body;
}

export function supportErrorKey(error: unknown, fallback: string): string {
  return error instanceof PostHogSupportError
    ? error.code
    : error instanceof SupportIdentityRequestError
      ? error.kind
      : fallback;
}

export function acknowledgedSupportTicket(
  response: {
    ticket_id: string;
    ticket_status: string;
    created_at: string;
    unread_count: number;
  },
  message: string,
  existing?: SupportTicket,
  requestedTitle?: string
): SupportTicket {
  const sentMessage: SupportMessage = {
    id: response.ticket_id,
    content: message,
    author: 'customer',
    createdAt: response.created_at,
  };
  return {
    id: response.ticket_id,
    status: response.ticket_status,
    title: deriveSupportTicketTitle(
      response.ticket_id,
      [sentMessage],
      requestedTitle?.trim() || existing?.title
    ),
    updatedAt: response.created_at,
    unreadCount: response.unread_count,
  };
}

export function putSupportTicketFirst(
  tickets: SupportTicket[],
  ticket: SupportTicket
): SupportTicket[] {
  return [ticket, ...tickets.filter((item) => item.id !== ticket.id)];
}

export function mergeSupportMessages(
  current: SupportMessage[],
  incoming: SupportMessage[]
): SupportMessage[] {
  const messages = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) messages.set(message.id, message);
  return [...messages.values()].sort((left, right) =>
    left.createdAt.localeCompare(right.createdAt)
  );
}

export function mergeSupportTicketPage(
  current: SupportTicket[],
  fresh: SupportTicket[]
): SupportTicket[] {
  return [
    ...fresh.map((ticket) => {
      const existing = current.find((item) => item.id === ticket.id);
      const existingTitle = existing?.title.trim();
      if (
        !existing ||
        !existingTitle ||
        existingTitle === getSupportTicketFallbackTitle(ticket.id)
      ) {
        return ticket;
      }
      return { ...ticket, title: existing.title };
    }),
    ...current.filter(
      (ticket) => !fresh.some((freshTicket) => freshTicket.id === ticket.id)
    ),
  ];
}

export function applySelectedTicket(
  tickets: SupportTicket[],
  selected: SelectedSupportTicket
): SupportTicket[] {
  const existing = tickets.find((ticket) => ticket.id === selected.id);
  const title = deriveSupportTicketTitle(
    selected.id,
    selected.messages,
    existing?.title
  );
  const updated = tickets.map((ticket) =>
    ticket.id === selected.id
      ? {
          ...ticket,
          status: selected.status,
          title,
          unreadCount: selected.unreadCount,
        }
      : ticket
  );
  if (updated.some((ticket) => ticket.id === selected.id)) return updated;
  const lastMessage = selected.messages.at(-1);
  return [
    {
      id: selected.id,
      status: selected.status,
      title,
      updatedAt: lastMessage?.createdAt ?? '',
      unreadCount: selected.unreadCount,
    },
    ...updated,
  ];
}
